import { ApolloProvider } from '@apollo/client';
import { Provider as ReduxProvider } from 'react-redux';
import { AuthError } from '@azure/msal-browser';
import { Button, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { getApolloClient } from 'src/services/apollo.service';
import {
  authDisabled,
  ensureSignedIn,
  getProvider,
} from 'src/services/auth.service';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import '@mui/styles';
import {
  ThemeProvider as MuiThemeProvider,
  Theme,
  StyledEngineProvider,
} from '@mui/material/styles';
import { materialOverrides } from 'src/config/material-overrides';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter } from 'react-router-dom';
import { theme } from 'src/config/theme';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { store } from 'src/store';
import { App } from './App';
import { useAppDispatch } from '../store/hooks';
import { useEffect, useState } from 'react';
import { UrlReactor } from './UrlReactor';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

const queryClient = new QueryClient();

export const Root: React.VFC = () => (
  <ReduxProvider store={store}>
    <MicrosoftAuth>
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={getApolloClient()}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StyledEngineProvider injectFirst>
              <MuiThemeProvider theme={materialOverrides}>
                <ThemeProvider theme={theme}>
                  <BrowserRouter>
                    <DndProvider backend={HTML5Backend}>
                      <UrlReactor />
                      <App />
                    </DndProvider>
                  </BrowserRouter>
                </ThemeProvider>
              </MuiThemeProvider>
            </StyledEngineProvider>
          </LocalizationProvider>
        </ApolloProvider>
      </QueryClientProvider>
    </MicrosoftAuth>
  </ReduxProvider>
);

// A failed sign-in used to unmount the whole app and show a blank page.
// Signing out clears every cached account, which is what an account the user
// no longer signs in as needs.
const SignInError: React.VFC<{ error: AuthError }> = ({ error }) => (
  <div style={{ padding: 32, display: 'grid', gap: 16, justifyItems: 'start' }}>
    <Typography variant="h6">Could not sign you in to Factory Planner</Typography>
    <Typography variant="body2">{error.errorMessage}</Typography>
    <Button variant="contained" onClick={() => getProvider().logoutRedirect()}>
      Sign out and try again
    </Button>
  </div>
);

const MicrosoftAuth: React.FC = ({ children }) => {
  if (authDisabled) {
    return <>{children}</>;
  }

  return <AuthGate>{children}</AuthGate>;
};

// The planner stays unmounted until the redirect hash is processed and an
// active account exists. Queries therefore cannot start a second sign-in.
const AuthGate: React.FC = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    ensureSignedIn()
      .then(() => setReady(true))
      .catch((err: unknown) => {
        setError(
          err instanceof AuthError
            ? err
            : new AuthError(
                'sign_in_failed',
                err instanceof Error ? err.message : 'Sign-in failed',
              ),
        );
      });
  }, []);

  if (error) return <SignInError error={error} />;
  if (!ready) return null;

  return <MicrosoftAuthInner>{children}</MicrosoftAuthInner>;
};

const MicrosoftAuthInner: React.FC = ({ children }) => {
  const dispatch = useAppDispatch();

  // If this component renders, we are logged in. We feed the account info to
  // redux. This happens only when the component is mounted
  useEffect(() => {
    dispatch({ type: 'AAD_LOGIN_SUCCESS' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};
